import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CompanyBiometricDeviceUpsertFormFields } from "@/components/companyBiometricDevices/CompanyBiometricDeviceUpsertFormFields";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { syncBiometricDevicesFromApi } from "@/lib/admin/biometricDevicesStore";
import { syncCompaniesFromApi } from "@/lib/admin/companiesStore";
import { createCompanyBiometricDevice } from "@/lib/admin/companyBiometricDevicesStore";
import { syncWarehousesFromApi } from "@/lib/catalog/warehousesStore";
import {
  companyBiometricDeviceFormErrorMessage,
  companyBiometricDeviceFormToPayload,
  emptyCompanyBiometricDeviceForm,
  type CompanyBiometricDeviceFormState,
} from "@/lib/admin/companyBiometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { ArrowLeft, Fingerprint, Loader2, Save } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const AddCompanyBiometricDevice = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "company_biometric_device_can_create");
  const showCompanyPicker = useMemo(
    () => !user?.company_id || can(user, "company_can_view_any"),
    [user],
  );
  const [form, setForm] = useState<CompanyBiometricDeviceFormState>(() =>
    emptyCompanyBiometricDeviceForm(user?.company_id ? String(user.company_id) : ""),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [loadingRefs, setLoadingRefs] = useState(true);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        await Promise.all([
          syncBiometricDevicesFromApi().catch(() => undefined),
          syncWarehousesFromApi({ activeOnly: true }).catch(() => undefined),
          showCompanyPicker ? syncCompaniesFromApi().catch(() => undefined) : Promise.resolve(),
        ]);
      } finally {
        if (mounted) setLoadingRefs(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showCompanyPicker]);

  useEffect(() => {
    if (user?.company_id && !showCompanyPicker) {
      setForm((f) => ({ ...f, companyId: String(user.company_id) }));
    }
  }, [user?.company_id, showCompanyPicker]);

  const handleCancel = () => navigate("/company-biometric-devices");

  const save = async () => {
    if (isSaving || !canCreate) return;
    if (showCompanyPicker && !form.companyId.trim()) {
      toast.error("Select a company.");
      return;
    }
    if (!form.biometricDeviceId.trim()) {
      toast.error("Select a device type from the catalog.");
      return;
    }
    if (!form.ipAddress.trim()) {
      toast.error("IP address is required.");
      return;
    }

    setIsSaving(true);
    try {
      const r = await createCompanyBiometricDevice(companyBiometricDeviceFormToPayload(form));
      toast.success(r.message);
      navigate("/company-biometric-devices");
    } catch (e) {
      console.error("[AddCompanyBiometricDevice] Save failed", e);
      toast.error(companyBiometricDeviceFormErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canCreate) {
    return (
      <PermissionDenied
        title="Cannot add company biometric devices"
        message="You don’t have permission to create company biometric devices. Ask an administrator to grant company_biometric_device_can_create."
      />
    );
  }

  return (
    <Layout title="Add company biometric device">
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button type="button" variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5" onClick={handleCancel}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">Add company biometric device</h1>
              <p className="text-sm text-muted-foreground mt-1">Link catalog hardware to a company with network settings.</p>
            </div>
          </div>
          <Button variant="premium" className="gap-2 shrink-0" disabled={isSaving || loadingRefs} onClick={() => void save()}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Fingerprint className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-foreground text-sm">Company hardware</h2>
              <p className="text-xs text-muted-foreground">Pick catalog model, then set IP, port, and location.</p>
            </div>
          </div>
          {loadingRefs ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading companies and device catalog…
            </p>
          ) : (
            <CompanyBiometricDeviceUpsertFormFields
              form={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              showCompanyPicker={showCompanyPicker}
            />
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default AddCompanyBiometricDevice;
